// ============================================
// DOCUMENT EDIT MODAL
// ============================================

import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, FileText, Loader2, Calendar, Save } from 'lucide-react';
import { useUpdateDocument } from '@/hooks';
import { useTranslation } from '@crew-at-sea/i18n';

interface DocumentEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: any;
  onSuccess?: () => void;
}

// Belge türü key'leri — çeviri panel:documents.types.* ile yapılıyor
const DOCUMENT_TYPE_KEYS = [
  'PASSPORT', 'CDC', 'CERTIFICATE', 'DIPLOMA',
  'MEDICAL', 'FLAG_DOCUMENT', 'CONTRACT', 'OTHER',
] as const;

export function DocumentEditModal({ open, onOpenChange, document, onSuccess }: DocumentEditModalProps) {
  const { t } = useTranslation(['panel', 'common']);
  const [name, setName] = useState('');
  const [type, setType] = useState('OTHER');
  const [expiryDate, setExpiryDate] = useState('');
  const [error, setError] = useState('');

  const updateDocumentMutation = useUpdateDocument();

  // Form'u belge değiştiğinde başlat
  useEffect(() => {
    if (document) {
      setName(document.title || document.name || '');
      setType(document.category || document.type || 'OTHER');
      setExpiryDate(
        document.expiryDate 
          ? new Date(document.expiryDate).toISOString().split('T')[0] 
          : ''
      );
    }
  }, [document]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError(t('panel:documents.documentNameRequired'));
      return;
    }

    setError('');

    try {
      await updateDocumentMutation.mutateAsync({
        id: document.id,
        data: {
          name: name.trim(),
          type: type,
          expiryDate: expiryDate ? new Date(expiryDate).toISOString() : undefined,
        },
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      setError(err?.message || t('panel:documents.updateError'));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-md z-[100]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {t('panel:documents.editTitle')}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-maritime hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            {t('panel:documents.editDescription')}
          </Dialog.Description>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-maritime text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Mevcut Dosya Bilgisi */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-maritime">
              <FileText className="w-8 h-8 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {document?.fileName || document?.title || document?.name}
                </p>
                {document?.fileSize && (
                  <p className="text-xs text-gray-500">
                    {(document.fileSize / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>
            </div>

            {/* Belge Adı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:documents.documentName')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input w-full"
                placeholder={t('panel:documents.documentNamePlaceholder')}
                required
              />
            </div>

            {/* Belge Türü */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:documents.documentType')} *
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="input w-full"
                required
              >
                {DOCUMENT_TYPE_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`panel:documents.types.${key}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Geçerlilik Tarihi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('panel:documents.expiryDateOptional')}
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="input w-full"
              />
            </div>

            {/* Aksiyonlar */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="btn-secondary"
                disabled={updateDocumentMutation.isPending}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={updateDocumentMutation.isPending}
              >
                {updateDocumentMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common:saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('common:save')}
                  </>
                )}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
